import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import emailjs from '@emailjs/browser';
import SEOMeta from '../components/SEOMeta/SEOMeta';
import './certificate.css';

const EMAILJS_SERVICE_ID = 'service_wzcrsxp';
const EMAILJS_DEFAULT_TEMPLATE_ID = 'template_t842edl';
const EMAILJS_CERTIFICATE_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_CERTIFICATE_TEMPLATE_ID
  || EMAILJS_DEFAULT_TEMPLATE_ID;
const EMAILJS_USER_ID = '7lTaAJbQz06mpRC3i';
const EMAIL_BATCH_SIZE = 10;
const EMAIL_BATCH_DELAY_MS = 1500;
const EMAIL_MAX_RETRIES = 2;
const EMAIL_RETRY_DELAY_MS = 2000;
const DEV_RETRY_EMAILS = new Set([
  'krishna1122singh1228@gmail.com',
  'vedkamth@gmail.com',
  'kumarannnu7522@gmail.com',
  'vaishnavidhakad245@gmail.com',
  'nandinikushwaha899@gmail.com',
  'mfbno3150@gmail.com',
  'uttamsingh5154@gmail.com',
  'muskanshrivastava0702@gmail.com',
  'namantarwari809@gmail.com',
  'hemantsahu7777777@gmail.com',
  'sagarphulore123@gmail.com',
  'sourya2506@gmail.com',
  'gvivek22134@gmail.com',
  'www.harsh12@gmail.com',
  'sanjanarohit2005@gmail.com',
  'rohitshilpkar9630@gmail.com',
  'vivekkumarbind2757@gmail.com',
  'riteshkumaryadav4215@gmail.com',
  'riyagupta472005@gmail.com',
  'kmanish02663@gmail.com',
  'koraniliya2005@gmail.com',
  'prabhatshukla0389@gmail.com',
  'divchauhan0258@gmail.com',
  'sameershrivastava1578@gmail.com',
  'mohiniverma594@gmail.com',
  'surajtiwari5023@gmail.com',
  'veerchauhan0143@gmail.com',
  'krm963101@gmail.com',
  'modirohitkingparihar2005@gmail.com',
  'prachichikhkhede23@gmail.com',
  'srajanchourasiya3200@gmail.com',
  'khushikumarkk076@gmail.com',
  'mamandubey1408@gmail.com',
  'subhnuapu1406@gmail.com',
  'vedbharat554@gmail.com',
  'kumarmausam1707@gmail.com',
  'kurtsantosh2145@gmail.com',
  'yuktiagrawal296@gmail.com',
  'yashkhatri0508@gmail.com',
  'vanidwivedi64@gmail.com',
]);

const normalizeKey = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const getRecordKey = (record) => `${normalizeKey(record?.name)}|${String(record?.email || '').toLowerCase().trim()}|${normalizeKey(record?.certId)}`;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const isListedDevRetryEmail = (email) => DEV_RETRY_EMAILS.has(String(email || '').toLowerCase().trim());

const getFieldValue = (row, possibleKeys) => {
  const keyMap = Object.keys(row || {}).reduce((acc, key) => {
    acc[normalizeKey(key)] = row[key];
    return acc;
  }, {});

  for (let i = 0; i < possibleKeys.length; i += 1) {
    const value = keyMap[normalizeKey(possibleKeys[i])];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }

  return '';
};

const buildCertificateTemplateParams = (emailData) => ({
  from_name: emailData.name,
  from_email: emailData.email,
  contact_number: 'Certificate',
  student_name: emailData.name,
  certificate_id: emailData.certId,
  verification_url: emailData.verificationUrl,
  reply_to: emailData.email,
  to_email: emailData.email,
  subject: `Your Algodoor Certificate - ${emailData.certId}`,
  message: `Congratulations ${emailData.name}!\n\nYour certificate is ready!\n\nCertificate ID: ${emailData.certId}\n\nVerify your certificate:\n${emailData.verificationUrl}\n\nHappy Learning!`,
});

const isRetryableEmailError = (error) => {
  const text = String(error?.text || error?.message || error || '');
  return /429|too many requests|failed to fetch|network/i.test(text);
};

const sendEmailWithRetry = async (emailData) => {
  const templateParams = buildCertificateTemplateParams(emailData);

  for (let attempt = 0; attempt <= EMAIL_MAX_RETRIES; attempt += 1) {
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_CERTIFICATE_TEMPLATE_ID,
        templateParams,
        EMAILJS_USER_ID,
      );
      return;
    } catch (error) {
      const isLastAttempt = attempt === EMAIL_MAX_RETRIES;
      if (isLastAttempt || !isRetryableEmailError(error)) {
        throw error;
      }
      await wait(EMAIL_RETRY_DELAY_MS * (attempt + 1));
    }
  }
};

const EmailSenderPage = () => {
  const [studentsCsv, setStudentsCsv] = useState(null);
  const [certificatesCsv, setCertificatesCsv] = useState(null);
  const [students, setStudents] = useState([]);
  const [certificateData, setCertificateData] = useState([]);
  const [matched, setMatched] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState('');
  const [error, setError] = useState('');
  const [emailSendResults, setEmailSendResults] = useState({});
  const [resendingRecordKey, setResendingRecordKey] = useState('');

  const isRecordSendable = (record) => (
    record.email && record.matched && record.certId && record.certId !== 'NOT_FOUND' && record.verificationUrl
  );

  // Match students with certificates
  const matchData = (studentsList, certList) => {
    if (!studentsList.length || !certList.length) return;

    const certMap = new Map();
    certList.forEach((cert) => {
      const certName = getFieldValue(cert, ['full_name', 'Full Name', 'name', 'student_name', 'student name']);
      if (certName) {
        certMap.set(certName.toLowerCase().trim(), cert);
      }
    });

    const matchedData = studentsList.map((student) => {
      const nameField = getFieldValue(student, ['Full Name', 'Name', 'Student Name', 'student_name']);
      const cert = certMap.get(nameField.toLowerCase().trim());

      return {
        name: nameField,
        email: getFieldValue(student, ['Email Address', 'Email', 'email', 'email_address']),
        certId: getFieldValue(cert || {}, ['certificate_id', 'certId', 'certificate id', 'cert id']) || 'NOT_FOUND',
        verificationUrl: getFieldValue(cert || {}, ['verification_url', 'verificationUrl', 'verification url', 'url']),
        matched: !!cert,
      };
    });

    setMatched(matchedData);
    setEmailSendResults({});
  };

  // Parse CSV/Excel file
  const parseFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(sheet);
        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });

  // Handle students CSV upload
  const handleStudentsCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    try {
      const data = await parseFile(file);
      setStudentsCsv(file.name);
      setStudents(data);
      setSendStatus('');
      matchData(data, certificateData);
    } catch (err) {
      setError(`Error parsing students file: ${err.message}`);
    }
  };

  // Handle certificates CSV upload
  const handleCertificatesCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    try {
      const data = await parseFile(file);
      setCertificatesCsv(file.name);
      setCertificateData(data);
      setSendStatus('');
      matchData(students, data);
    } catch (err) {
      setError(`Error parsing certificates file: ${err.message}`);
    }
  };

  // Send emails using EmailJS - Same config as Modal/Get a Quote
  const handleSendEmails = async () => {
    if (!matched.length) {
      setError('No matched records to send');
      return;
    }

    const unmatchedCount = matched.filter((m) => !m.matched).length;
    if (unmatchedCount > 0) {
      const confirmSend = window.confirm(
        `Warning: ${unmatchedCount} students have no matching certificates.\n\nContinue sending to matched students only?`
      );
      if (!confirmSend) return;
    }

    const validEmails = matched.filter((m) => isRecordSendable(m));
    if (!validEmails.length) {
      setError('No valid student records to send. Ensure email, certificate ID, and verification URL are present.');
      return;
    }

    setSending(true);
    setError('');
    setSendStatus('Sending emails...');

    const sendingMap = {};
    validEmails.forEach((record) => {
      sendingMap[getRecordKey(record)] = { status: 'sending' };
    });
    setEmailSendResults((prev) => ({ ...prev, ...sendingMap }));

    try {
      let sentCount = 0;
      let failedCount = 0;
      setSendStatus(`Sending emails in batches... (0/${validEmails.length})`);

      for (let i = 0; i < validEmails.length; i += EMAIL_BATCH_SIZE) {
        const batch = validEmails.slice(i, i + EMAIL_BATCH_SIZE);
        const results = await Promise.allSettled(batch.map((emailData) => sendEmailWithRetry(emailData)));

        const resultMap = {};
        results.forEach((result, index) => {
          const emailData = batch[index];
          const recordKey = getRecordKey(emailData);
          if (result.status === 'rejected') {
            failedCount += 1;
            resultMap[recordKey] = { status: 'failed' };
            console.error(`Failed to send to ${emailData.email}:`, result.reason);
          } else {
            sentCount += 1;
            resultMap[recordKey] = { status: 'sent' };
          }
        });

        setEmailSendResults((prev) => ({ ...prev, ...resultMap }));
        setSendStatus(`Sending emails in batches... (${sentCount + failedCount}/${validEmails.length})`);

        if (i + EMAIL_BATCH_SIZE < validEmails.length) {
          // Small pause between batches to avoid provider rate limiting.
          // eslint-disable-next-line no-await-in-loop
          await wait(EMAIL_BATCH_DELAY_MS);
        }
      }

      const message = `✅ Successfully sent ${sentCount} emails!${failedCount > 0 ? ` (${failedCount} failed)` : ''}`;
      setSendStatus(message);
    } catch (err) {
      setError(`Email sending failed: ${err.message}`);
      setSendStatus('');
    } finally {
      setSending(false);
    }
  };

  const handleResendSingleEmail = async (record) => {
    if (!isRecordSendable(record)) {
      setError('This row is not eligible for sending. Check email, certificate ID, and verification URL.');
      return;
    }

    const recordKey = getRecordKey(record);
    setResendingRecordKey(recordKey);
    setError('');
    setEmailSendResults((prev) => ({
      ...prev,
      [recordKey]: { status: 'sending' },
    }));

    try {
      await sendEmailWithRetry(record);
      setEmailSendResults((prev) => ({
        ...prev,
        [recordKey]: { status: 'sent' },
      }));
      setSendStatus(`✅ Resent successfully to ${record.email}`);
    } catch (err) {
      setEmailSendResults((prev) => ({
        ...prev,
        [recordKey]: { status: 'failed' },
      }));
      setError(`Resend failed for ${record.email}: ${err.message}`);
    } finally {
      setResendingRecordKey('');
    }
  };

  const handleSendListedEmails = async () => {
    if (!matched.length) {
      setError('No matched records to send');
      return;
    }

    const listedRecords = matched.filter((record) => isRecordSendable(record) && isListedDevRetryEmail(record.email));
    if (!listedRecords.length) {
      setError('No listed email records found in current matched data.');
      return;
    }

    setSending(true);
    setError('');
    setSendStatus('Sending listed emails...');

    const sendingMap = {};
    listedRecords.forEach((record) => {
      sendingMap[getRecordKey(record)] = { status: 'sending' };
    });
    setEmailSendResults((prev) => ({ ...prev, ...sendingMap }));

    try {
      let sentCount = 0;
      let failedCount = 0;
      setSendStatus(`Sending listed emails in batches... (0/${listedRecords.length})`);

      for (let i = 0; i < listedRecords.length; i += EMAIL_BATCH_SIZE) {
        const batch = listedRecords.slice(i, i + EMAIL_BATCH_SIZE);
        const results = await Promise.allSettled(batch.map((emailData) => sendEmailWithRetry(emailData)));

        const resultMap = {};
        results.forEach((result, index) => {
          const emailData = batch[index];
          const recordKey = getRecordKey(emailData);
          if (result.status === 'rejected') {
            failedCount += 1;
            resultMap[recordKey] = { status: 'failed' };
            console.error(`Failed to send to ${emailData.email}:`, result.reason);
          } else {
            sentCount += 1;
            resultMap[recordKey] = { status: 'sent' };
          }
        });

        setEmailSendResults((prev) => ({ ...prev, ...resultMap }));
        setSendStatus(`Sending listed emails in batches... (${sentCount + failedCount}/${listedRecords.length})`);

        if (i + EMAIL_BATCH_SIZE < listedRecords.length) {
          // eslint-disable-next-line no-await-in-loop
          await wait(EMAIL_BATCH_DELAY_MS);
        }
      }

      setSendStatus(`✅ Listed resend complete: ${sentCount} sent${failedCount > 0 ? ` (${failedCount} failed)` : ''}`);
    } catch (err) {
      setError(`Listed resend failed: ${err.message}`);
      setSendStatus('');
    } finally {
      setSending(false);
    }
  };

  const validSendCount = matched.filter((m) => isRecordSendable(m)).length;
  const listedSendCount = matched.filter((m) => isRecordSendable(m) && isListedDevRetryEmail(m.email)).length;
  const filesSelected = Boolean(studentsCsv && certificatesCsv);

  return (
    <div className="cert-page">
      <SEOMeta
        title="Email Campaign Manager | Algodoor Training Platform"
        description="Send bulk personalized emails to students with templates and delivery tracking."
        keywords="email sender, bulk email, campaign manager"
        url="/emailsender"
        type="WebPage"
        noindex
      />
      <div className="cert-shell">
        <div className="cert-header">
          <div>
            <h1>Email Sender</h1>
            <p className="cert-sub">Send certificates to students via email</p>
          </div>
          <a className="cert-link" href="/">Back to Home</a>
        </div>

        <div className="cert-grid">
          {/* Students CSV Upload */}
          <div className="cert-card">
            <h2>Step 1: Student Information</h2>
            <p className="cert-sub">CSV/Excel with Full Name and Email Address</p>
            <div className="cert-field">
              <label htmlFor="students-csv">Upload Students CSV</label>
              <input
                id="students-csv"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleStudentsCsvUpload}
              />
            </div>
            {studentsCsv && <p className="cert-help">✅ Loaded: {studentsCsv}</p>}
            {students.length > 0 && (
              <p className="cert-help">📊 {students.length} students found</p>
            )}
          </div>

          {/* Certificates CSV Upload */}
          <div className="cert-card">
            <h2>Step 2: Certificate Data</h2>
            <p className="cert-sub">CSV/Excel with full_name and verification_url</p>
            <div className="cert-field">
              <label htmlFor="certificates-csv">Upload Certificates CSV</label>
              <input
                id="certificates-csv"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleCertificatesCsvUpload}
              />
            </div>
            {certificatesCsv && <p className="cert-help">✅ Loaded: {certificatesCsv}</p>}
            {certificateData.length > 0 && (
              <p className="cert-help">📊 {certificateData.length} certificates found</p>
            )}
          </div>
        </div>

        {/* Send Button */}
        <div className="cert-actions" style={{ marginTop: '1rem' }}>
          <button
            type="button"
            className="cert-btn"
            onClick={handleSendEmails}
            disabled={sending || !filesSelected}
          >
            {sending ? 'Sending All Emails...' : `Send All Emails (${validSendCount})`}
          </button>
          <button
            type="button"
            className="cert-btn"
            onClick={handleSendListedEmails}
            disabled={sending || !filesSelected || listedSendCount === 0}
            style={{ marginLeft: '0.75rem' }}
          >
            {sending ? 'Sending Listed...' : `Send Listed Failed (${listedSendCount})`}
          </button>
          {filesSelected && validSendCount === 0 && (
            <p className="cert-help" style={{ marginTop: '0.5rem' }}>
              No valid matched rows found yet. Check name matching and required fields in both files.
            </p>
          )}
        </div>

        {/* Error Messages */}
        {error && <div className="cert-error">❌ {error}</div>}
        {sendStatus && <div className="cert-progress">ℹ️ {sendStatus}</div>}

        {/* Matched Data Preview */}
        {matched.length > 0 && (
          <div className="cert-upload-table-wrap">
            <h3>Preview: Matched Records</h3>
            <div style={{ overflowX: 'auto' }}>
              <table className="cert-upload-table">
                <thead>
                  <tr>
                    <th>Full Name</th>
                    <th>Email</th>
                    <th>Certificate ID</th>
                    <th>Status</th>
                    <th>Email Status</th>
                  </tr>
                </thead>
                <tbody>
                  {matched.map((record, idx) => (
                    <tr key={idx} style={{ opacity: record.matched ? 1 : 0.5 }}>
                      <td>{record.name}</td>
                      <td>{record.email || '—'}</td>
                      <td>{record.certId}</td>
                      <td>
                        {record.matched ? (
                          <span style={{ color: '#166534', fontWeight: 600 }}>✅ Matched</span>
                        ) : (
                          <span style={{ color: '#991b1b', fontWeight: 600 }}>⚠️ Not Found</span>
                        )}
                      </td>
                      <td>
                        {emailSendResults[getRecordKey(record)]?.status === 'sent' && (
                          <span style={{ color: '#166534', fontWeight: 600 }}>✅ Sent</span>
                        )}
                        {emailSendResults[getRecordKey(record)]?.status === 'failed' && (
                          <>
                            <span style={{ color: '#991b1b', fontWeight: 600, marginRight: '0.5rem' }}>❌ Failed</span>
                            <button
                              type="button"
                              className="cert-btn"
                              onClick={() => handleResendSingleEmail(record)}
                              disabled={sending || (resendingRecordKey && resendingRecordKey !== getRecordKey(record))}
                              style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                            >
                              {resendingRecordKey === getRecordKey(record) ? 'Resending...' : 'Resend'}
                            </button>
                          </>
                        )}
                        {emailSendResults[getRecordKey(record)]?.status === 'sending' && (
                          <span style={{ color: '#1d4ed8', fontWeight: 600 }}>⏳ Sending</span>
                        )}
                        {!emailSendResults[getRecordKey(record)]?.status && !isRecordSendable(record) && (
                          <span style={{ color: '#7c2d12', fontWeight: 600 }}>⚠️ Not Eligible</span>
                        )}
                        {!emailSendResults[getRecordKey(record)]?.status && isRecordSendable(record) && (
                          <span style={{ color: '#475569', fontWeight: 600 }}>• Pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="cert-sub" style={{ marginTop: '2rem', color: '#52657d' }}>
          📋
          {' '}
          <strong>CSV Format Required:</strong>
          <br />
          Students CSV: Columns must include &quot;Full Name&quot; and &quot;Email Address&quot;
          <br />
          Certificates CSV: Columns must include &quot;full_name&quot; and &quot;verification_url&quot;
          <br />
          Certificate template override: set <strong>REACT_APP_EMAILJS_CERTIFICATE_TEMPLATE_ID</strong> to use a separate EmailJS template for certificate emails.
        </p>
      </div>
    </div>
  );
};

export default EmailSenderPage;
